import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: unknown }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: unknown }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    let syncVersion = 0;

    const syncSession = async (currentSession: Session | null) => {
      if (!mounted) return;

      const currentVersion = ++syncVersion;
      setLoading(true);
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      let adminStatus = false;
      if (currentSession?.user) {
        adminStatus = await checkAdminStatus(currentSession.user.id);
      }

      if (mounted && currentVersion === syncVersion) {
        setIsAdmin(adminStatus);
        setLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {
        // Supabase invokes this callback while holding its auth lock. Defer
        // the database lookup so sign-in cannot deadlock on the same client.
        window.setTimeout(() => {
          void syncSession(currentSession);
        }, 0);
      },
    );

    // Check for an existing session once when the provider mounts.
    void supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      void syncSession(currentSession);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkAdminStatus = async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();

      if (error) {
        console.error('Error checking admin status:', error.code ?? 'unknown');
        return false;
      }

      return !!data;
    } catch (error) {
      console.error(
        'Error in checkAdminStatus:',
        error instanceof Error ? error.name : 'unknown',
      );
      return false;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      // Navigation is handled by the Auth page after the session event.
      return { error };
    } catch (error: unknown) {
      return { error };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      // Preserve ?next= from the current URL so email verification returns the
      // user to the original destination (e.g. an OAuth consent page).
      const params = new URLSearchParams(window.location.search);
      const rawNext = params.get('next');
      const next = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/';
      const redirectUrl = `${window.location.origin}${next}`;

      const { error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
          },
        },
      });

      // Navigation is handled by the Auth page after the session event.
      return { error };
    } catch (error: unknown) {
      return { error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
    navigate('/auth');
  };

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
